import { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import ThemeToggle from './ThemeToggle'
import { useTheme } from '../hooks/useTheme'

export default function Navigation() {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const { theme } = useTheme()

  // Close mobile menu on route change
  useEffect(() => {
    setIsOpen(false)
  }, [location.pathname])

  const navItems = [
    { name: 'Home', href: '/' },
    { name: 'About', href: '/about' },
    { name: 'Team', href: '/team' },
    { name: 'Projects', href: '/projects' },
    { name: 'Contact', href: '/contact' },
  ]

  const isActive = (href: string) => {
    if (href === '/') return location.pathname === '/'
    return location.pathname.startsWith(href)
  }

  return (
    <nav className="nav-frosted">
      <div className="container-page h-full flex items-center justify-between">
        {/* Logo */}
        <Link 
          to="/" 
          onClick={(e) => {
            if (window.location.pathname === '/') {
              e.preventDefault()
              window.scrollTo({ top: 0, behavior: 'smooth' })
            }
          }}
          className="flex items-center gap-3 no-underline flex-shrink-0"
        >
          <img 
            src={theme === 'dark' ? '/logo-dark.png' : '/logo-light.png'} 
            alt="Ionic Errrrs Code Logo" 
            className="w-[30px] h-[30px] object-contain rounded-md" 
          />
          <span className="font-display text-[1rem] sm:text-[1.12rem] font-semibold tracking-tight whitespace-nowrap" style={{ color: 'var(--text)', letterSpacing: '-.02em' }}>
            Ionic Errrrs Code
          </span>
        </Link>

        {/* Desktop Nav Links */}
        <div className="hidden md:flex items-center gap-9">
          {navItems.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={(e) => {
                if (item.href === '/' && window.location.pathname === '/') {
                  e.preventDefault()
                  window.scrollTo({ top: 0, behavior: 'smooth' })
                }
              }}
              className="text-[.93rem] font-medium no-underline transition-colors duration-300"
              style={{ color: isActive(item.href) ? 'var(--text)' : 'var(--muted)' }}
              onMouseEnter={(e) => { (e.target as HTMLElement).style.color = 'var(--text)' }}
              onMouseLeave={(e) => { if (!isActive(item.href)) (e.target as HTMLElement).style.color = 'var(--muted)' }}
            >
              {item.name}
            </Link>
          ))}
        </div>

        {/* Right side: Theme Toggle + CTA + Mobile Menu */}
        <div className="flex items-center gap-3">
          <ThemeToggle />
          
          <div className="hidden md:block">
            <Link to="/start-project" className="btn-pill btn-primary text-sm">
              Start a project
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden w-10 h-10 flex flex-col justify-center items-center gap-[5px] cursor-pointer"
            onClick={() => setIsOpen(!isOpen)}
            aria-label="Toggle menu"
            style={{ background: 'none', border: 'none' }}
          >
            <span
              className="block w-5 h-[1.5px] transition-all duration-300 origin-center"
              style={{
                background: 'var(--text)',
                transform: isOpen ? 'rotate(45deg) translateY(3.25px)' : 'none',
              }}
            />
            <span
              className="block w-5 h-[1.5px] transition-all duration-300"
              style={{
                background: 'var(--text)',
                opacity: isOpen ? 0 : 1,
              }}
            />
            <span
              className="block w-5 h-[1.5px] transition-all duration-300 origin-center"
              style={{
                background: 'var(--text)',
                transform: isOpen ? 'rotate(-45deg) translateY(-3.25px)' : 'none',
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <div
          className="md:hidden absolute left-0 right-0 border-b"
          style={{
            top: '74px',
            background: 'var(--bg)',
            borderColor: 'var(--border)',
            zIndex: 99,
          }}
        >
          <div className="container-page py-6 flex flex-col gap-4">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => {
                  setIsOpen(false)
                  if (item.href === '/' && window.location.pathname === '/') {
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }
                }}
                className="text-[.93rem] font-medium no-underline py-2 transition-colors duration-300"
                style={{ color: isActive(item.href) ? 'var(--text)' : 'var(--muted)' }}
              >
                {item.name}
              </Link>
            ))}
            <Link
              to="/start-project"
              onClick={() => setIsOpen(false)}
              className="btn-pill btn-primary text-center mt-2"
            >
              Start a project
            </Link>
          </div>
        </div>
      )}
    </nav>
  )
}
